import type { VectorState } from '../core/types';

export const EPSILON = 1e-8;

export function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

export function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

export function lerp(from: number, to: number, amount: number): number {
  return from + (to - from) * amount;
}

export function damp(from: number, to: number, sharpness: number, deltaSeconds: number): number {
  return lerp(from, to, 1 - Math.exp(-Math.max(0, sharpness) * deltaSeconds));
}

export function moveTowards(value: number, target: number, maximumDelta: number): number {
  if (Math.abs(target - value) <= maximumDelta) return target;
  return value + Math.sign(target - value) * maximumDelta;
}

export function dot(a: VectorState, b: VectorState): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

export function length(vector: VectorState): number {
  return Math.hypot(vector.x, vector.y, vector.z);
}

export function horizontalLength(vector: VectorState): number {
  return Math.hypot(vector.x, vector.z);
}

export function normalize(vector: VectorState, fallback: VectorState = { x: 0, y: 0, z: 1 }): VectorState {
  const magnitude = length(vector);
  if (magnitude <= EPSILON) return { ...fallback };
  return { x: vector.x / magnitude, y: vector.y / magnitude, z: vector.z / magnitude };
}

export function horizontalNormalize(
  vector: VectorState,
  fallback: VectorState = { x: 0, y: 0, z: 1 },
): VectorState {
  const magnitude = horizontalLength(vector);
  if (magnitude <= EPSILON) return { ...fallback, y: 0 };
  return { x: vector.x / magnitude, y: 0, z: vector.z / magnitude };
}

export function distanceSquared(a: VectorState, b: VectorState): number {
  const x = a.x - b.x;
  const y = a.y - b.y;
  const z = a.z - b.z;
  return x * x + y * y + z * z;
}

export function planarDistance(a: VectorState, b: VectorState): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

export function yawForward(yaw: number): VectorState {
  return { x: Math.sin(yaw), y: 0, z: Math.cos(yaw) };
}

export function yawRight(yaw: number): VectorState {
  return { x: Math.cos(yaw), y: 0, z: -Math.sin(yaw) };
}

export function yawFromForward(forward: VectorState): number {
  return Math.atan2(forward.x, forward.z);
}

export function copyVector(vector: VectorState): VectorState {
  return { x: vector.x, y: vector.y, z: vector.z };
}

export function finiteOr(value: number | undefined, fallback: number): number {
  return value !== undefined && Number.isFinite(value) ? value : fallback;
}

